/**
 * Linha do tempo ÚNICA do atendimento (spec 018, US3 / FR-005).
 *
 * Medido em `5c943de`: o ticket tinha 8 abas, e a conversa do WhatsApp (aba
 * Conversas) e os comentários (aba Comunicação) eram dois fluxos separados do
 * mesmo atendimento — exatamente a fronteira Blip × TiFlux que o ERP veio
 * apagar. Aqui entra tudo, em ordem: comentários públicos e notas internas,
 * mensagens das conversas vinculadas, eventos do sistema, apontamentos e OS.
 *
 * Paginado do MAIS NOVO para trás (`cursor` = o mais antigo da página
 * anterior); cada página volta em ordem de leitura (mais antigo primeiro).
 */
import { z } from 'zod';
import { ChatAuthorTypeSchema, ChatContentTypeSchema, ChatOriginSchema } from './chats';
import { Dateish } from './common';
import { ServiceOrderStatusSchema, ServiceOrderTypeSchema } from './service-orders';
import { TicketEventTypeSchema } from './tickets';
import { DeliveryErrorSchema, DeliveryStatusSchema } from './whatsapp';
import { CommentAuthorKindSchema } from './work-views';

export const TIMELINE_KINDS = [
  'comment',
  'chat_message',
  'chat_restricted',
  'event',
  'appointment',
  'service_order',
] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number];

const TimelineFileSchema = z.object({
  id: z.number().int(),
  fileName: z.string(),
  size: z.number().int(),
  contentType: z.string(),
});

export const TimelineEntrySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('comment'),
    id: z.number().int(),
    at: Dateish,
    authorKind: CommentAuthorKindSchema,
    authorName: z.string(),
    userId: z.number().int().nullable(),
    body: z.string(),
    internal: z.boolean(),
    files: z.array(TimelineFileSchema),
    /** spec 037 (PR C): menções da comunicação interna; ausente em API antiga */
    mentions: z.array(z.object({ userId: z.number().int(), name: z.string() })).optional(),
  }),
  z.object({
    kind: z.literal('chat_message'),
    /** id da MENSAGEM */
    id: z.number().int(),
    at: Dateish,
    chatId: z.number().int(),
    chatOrigin: ChatOriginSchema,
    direction: z.enum(['inbound', 'outbound']),
    authorType: ChatAuthorTypeSchema,
    authorName: z.string().nullable(),
    internal: z.boolean(),
    text: z.string(),
    contentType: ChatContentTypeSchema,
    mediaMime: z.string().nullable(),
    mediaFilename: z.string().nullable(),
    mediaSize: z.number().int().nullable(),
    caption: z.string().nullable(),
    /** rota de serving da mídia (a mesma do /chats) — nunca a chave do storage */
    mediaUrl: z.string().nullable(),
    deliveryStatus: DeliveryStatusSchema.nullable(),
    /** com 'failed', o motivo curto (spec 028) — ausente em resposta de API antiga */
    deliveryError: DeliveryErrorSchema.nullable().optional(),
    /** o "tentar de novo" já reenviou esta mensagem (spec 028) — só nas Conversas */
    resentAt: Dateish.nullable().optional(),
  }),
  /**
   * Conversa vinculada que ESTA pessoa não pode ler (mesa de outro grupo, ou
   * sem acesso a conversas): aparece que existe, nunca o conteúdo — nem autor,
   * nem texto, nem contato.
   */
  z.object({
    kind: z.literal('chat_restricted'),
    /** id da CONVERSA */
    id: z.number().int(),
    at: Dateish,
    messages: z.number().int(),
  }),
  z.object({
    kind: z.literal('event'),
    id: z.number().int(),
    at: Dateish,
    type: TicketEventTypeSchema,
    userName: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
  }),
  z.object({
    kind: z.literal('appointment'),
    id: z.number().int(),
    /** o início do trabalho: apontamento retroativo entra onde o trabalho aconteceu */
    at: Dateish,
    userName: z.string(),
    startedAt: Dateish,
    /** null = cronômetro correndo */
    endedAt: Dateish.nullable(),
    description: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('service_order'),
    id: z.number().int(),
    at: Dateish,
    number: z.number().int(),
    type: ServiceOrderTypeSchema,
    status: ServiceOrderStatusSchema,
  }),
]);
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;

export const TIMELINE_PAGE_DEFAULT = 100;
export const TIMELINE_PAGE_MAX = 200;

export const TicketTimelineQuerySchema = z.object({
  cursor: z.string().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(TIMELINE_PAGE_MAX).default(TIMELINE_PAGE_DEFAULT),
});
export type TicketTimelineQuery = z.infer<typeof TicketTimelineQuerySchema>;

export const TicketTimelineSchema = z.object({
  /** mais antigo primeiro (ordem de leitura) */
  entries: z.array(TimelineEntrySchema),
  /** cursor da página ANTERIOR (mais antiga); null = começo do atendimento */
  nextCursor: z.string().nullable(),
});
export type TicketTimeline = z.infer<typeof TicketTimelineSchema>;

/**
 * Eventos do sistema que já aparecem como o próprio item (o comentário, o
 * apontamento, a OS) — repeti-los na linha do tempo seria dizer tudo duas vezes.
 */
export const TIMELINE_HIDDEN_EVENT_TYPES = [
  'comment_added',
  'appointment_added',
  'service_order_created',
  // spec 022: a nota do bot aparece como ela mesma (comentário, autor "Bot")
  'bot_note_added',
] as const;

// ── Ordem e cursor ──────────────────────────────────────────────────────────
/** Desempate estável de itens no MESMO instante (o tipo, depois o id). */
const KIND_ORDER: Record<TimelineKind, number> = Object.fromEntries(
  TIMELINE_KINDS.map((k, i) => [k, i])
) as Record<TimelineKind, number>;

export interface TimelinePosition {
  at: number;
  kind: TimelineKind;
  id: number;
}

export function timelinePosition(entry: { at: Date | string; kind: TimelineKind; id: number }): TimelinePosition {
  return { at: new Date(entry.at).getTime(), kind: entry.kind, id: entry.id };
}

/** Ordem cronológica total: instante, tipo, id. */
export function compareTimeline(a: TimelinePosition, b: TimelinePosition): number {
  return a.at - b.at || KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.id - b.id;
}

/** `<ms>.<tipo>.<id>` — opaco para a tela, legível no log. */
export function encodeTimelineCursor(p: TimelinePosition): string {
  return `${p.at}.${p.kind}.${p.id}`;
}

export function decodeTimelineCursor(raw: string): TimelinePosition | null {
  const m = /^(\d{1,15})\.([a-z_]+)\.(\d{1,10})$/.exec(raw);
  if (!m) return null;
  const kind = m[2] as TimelineKind;
  if (!(TIMELINE_KINDS as readonly string[]).includes(kind)) return null;
  return { at: Number(m[1]), kind, id: Number(m[3]) };
}

/**
 * Uma página da linha do tempo a partir de candidatos já lidos de cada fonte
 * (cada fonte lê os seus N mais recentes ANTES do cursor): junta, ordena, corta
 * no cursor e devolve os `limit` mais recentes em ordem de leitura. `more` diz
 * se alguma fonte ainda tinha o que dar — então há página anterior.
 */
export function pageTimeline<T extends { at: Date | string; kind: TimelineKind; id: number }>(
  candidates: readonly T[],
  opts: { cursor: TimelinePosition | null; limit: number; sourcesExhausted: boolean }
): { entries: T[]; nextCursor: string | null } {
  const before = opts.cursor;
  const eligible = before
    ? candidates.filter((c) => compareTimeline(timelinePosition(c), before) < 0)
    : [...candidates];
  eligible.sort((a, b) => compareTimeline(timelinePosition(b), timelinePosition(a)));
  const page = eligible.slice(0, opts.limit);
  const more = eligible.length > opts.limit || !opts.sourcesExhausted;
  const oldest = page[page.length - 1];
  return {
    entries: page.reverse(),
    nextCursor: more && oldest ? encodeTimelineCursor(timelinePosition(oldest)) : null,
  };
}
